import React, { useState, useEffect, useMemo } from "react";
import { Line as LineChart } from "react-chartjs-2";
import "chart.js/auto";
import { MDBContainer, MDBRow, MDBCol, MDBCard, MDBCardBody } from "mdb-react-ui-kit";
import { api } from "../../services";
import Toolbar from "../Toolbar";
import { KpiCard, DataTable, Tag } from "../ui";
import { useTranslation } from "react-i18next";

function AdminDashboard() {
  const { t } = useTranslation("translation");
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tableSortColumn, setTableSortColumn] = useState("username");
  const [sortDirection, setSortDirection] = useState("desc");

  useEffect(() => {
    async function fetchUsers() {
      try {
        const token = localStorage.getItem("adminToken");
        const response = await api.get("/admin/users", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const nonAdminUsers = response.data.users.filter(
          (user) => user.user_type !== "admin"
        );
        setUsers(nonAdminUsers);
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchUsers();
  }, []);

  useEffect(() => {
    async function fetchChartData() {
      try {
        const response = await api.get("/demo-api/metrics");
        const data = response.data;
        setChartData({
          labels: data.map((item) => item.date),
          datasets: [
            {
              label: "User Activity",
              data: data.map((item) => item.value),
              backgroundColor: "rgba(75, 192, 192, 0.2)",
              borderColor: "rgba(75, 192, 192, 1)",
              borderWidth: 1,
            },
          ],
        });
      } catch (error) {
        console.error("Error fetching chart data:", error);
      }
    }
    fetchChartData();
  }, []);

  const filteredUsers = useMemo(() => {
    const query = search.toLowerCase();
    return users.filter(
      (user) =>
        (user.username || "").toLowerCase().includes(query) ||
        (user.email || "").toLowerCase().includes(query)
    );
  }, [users, search]);

  const sortedUsers = useMemo(() => {
    const list = [...filteredUsers];
    const dir = sortDirection === "asc" ? 1 : -1;
    list.sort((a, b) => {
      let av;
      let bv;
      switch (tableSortColumn) {
        case "email":
          av = (a.email || "").toLowerCase();
          bv = (b.email || "").toLowerCase();
          break;
        case "phone":
          av = (a.phone || "").toLowerCase();
          bv = (b.phone || "").toLowerCase();
          break;
        case "createdAt":
          av = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bv = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dir * (av - bv);
        case "status":
          av = (a.status || "").toLowerCase();
          bv = (b.status || "").toLowerCase();
          break;
        default:
          av = (a.username || "").toLowerCase();
          bv = (b.username || "").toLowerCase();
      }
      if (typeof av === "string") return dir * av.localeCompare(bv);
      return 0;
    });
    return list;
  }, [filteredUsers, tableSortColumn, sortDirection]);

  const getStatusVariant = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "active") return "success";
    if (s === "inactive") return "error";
    return "warning";
  };

  const columns = useMemo(
    () => [
      {
        id: "username",
        header: t("name"),
        sortable: true,
        strong: true,
        render: (user) => user.username,
      },
      {
        id: "email",
        header: t("email"),
        sortable: true,
        render: (user) => user.email,
      },
      {
        id: "phone",
        header: t("phone"),
        sortable: true,
        render: (user) => user.phone || "—",
      },
      {
        id: "createdAt",
        header: t("joiningdate"),
        sortable: true,
        render: (user) =>
          user.createdAt
            ? new Date(user.createdAt).toLocaleDateString()
            : "—",
      },
      {
        id: "status",
        header: t("status"),
        sortable: true,
        render: (user) => (
          <Tag variant={getStatusVariant(user.status)}>
            {t(user.status) || user.status || "Active"}
          </Tag>
        ),
      },
    ],
    [t]
  );

  return (
    <div className="admin-page-root oh-page">
      <MDBContainer fluid className="content-area flex-grow-1">
        <h1 className="page-title-header">{t("admindashboard")}</h1>

        <div className="oh-page-section">
          <MDBRow className="g-4">
            <MDBCol md="3">
              <KpiCard
                title={t("totalusers")}
                value={filteredUsers.length}
                accentColor="#083B4C"
                className="h-100"
              />
            </MDBCol>
          </MDBRow>
        </div>

        <div className="oh-page-section">
          <Toolbar
            showSearch
            searchPlaceHolder={t("Search Users")}
            searchQuery={search}
            handleSearch={(e) => setSearch(e.target.value)}
          />
        </div>

        {chartData && (
          <div className="oh-page-section">
            <MDBCard>
              <MDBCardBody>
                <h5 className="mb-4">{t("User Activity")}</h5>
                <LineChart data={chartData} />
              </MDBCardBody>
            </MDBCard>
          </div>
        )}

        <div className="oh-page-section">
          <DataTable
            tableId="admin-dashboard-users"
            title={t("userlist")}
            columns={columns}
            data={sortedUsers}
            rowKey="_id"
            loading={loading}
            selectionMode="index"
            emptyState={t("No data available")}
            sort={{
              column: tableSortColumn,
              direction: sortDirection,
              onChange: ({ column, direction }) => {
                setTableSortColumn(column);
                setSortDirection(direction);
              },
            }}
            pagination={{
              pageSizeOptions: [10, 20, 50],
              showLabel: t("show"),
              ofLabel: t("of", { defaultValue: "of" }),
              resultsLabel: t("results", { defaultValue: "results" }),
              previousLabel: t("previous"),
              nextLabel: t("next"),
            }}
          />
        </div>
      </MDBContainer>
    </div>
  );
}

export default AdminDashboard;
